import Vue from 'vue'
import to from 'await-to-js'
import Axios from '@/config/axios'
import client from '@/api/client'
import userExistsQuery from '@/api/gql/account/query/user-exists.gql'
import currentUserQuery from '@/api/gql/account/query/current-user.gql'
import savedPaymentsQuery from '@/api/gql/account/query/saved-payments.gql'
import deleteSavedPaymentMutation from '@/api/gql/account/mutation/delete-saved-payment.gql'
import { areAddressesEqual } from '@/helpers/address'

const FETCH_STATES = {
  initial: 0,
  fetching: 1,
  done: 2,
}
let savedPaymentsFetchState = FETCH_STATES.initial

const getDefaultState = () => {
  return {
    currentUser: null,
    hasFetchedCurrentUser: false,
    paths: {},
    userFetchState: FETCH_STATES.initial,
  }
}

export default {
  namespaced: true,
  state: getDefaultState(),

  mutations: {
    setState(state, payload) {
      state[payload[0]] = payload[1]
    },

    setInitialState(state) {
      const defaultState = getDefaultState()
      for (const key in defaultState) {
        state[key] = defaultState[key]
      }
    },

    setUserData(state, payload) {
      state.currentUser = payload ? { ...state.currentUser, ...payload } : payload
      state.hasFetchedCurrentUser = true
    },

    setAccountPaths(state, paths) {
      state.paths = { ...state.paths, ...paths }
    },

    pushToAddresses(state, addressObject) {
      state.currentUser.addresses.push(addressObject)
    },

    deleteAddress(state, addressIndex) {
      state.currentUser.addresses.splice(addressIndex, 1)
    },

    setAddress(state, payload) {
      Vue.set(state.currentUser.addresses, payload[0], payload[1])
    },

    setAddressIsDefault(state, payload) {
      const address = state.currentUser.addresses[payload[0]]
      Vue.set(address, 'is_default', payload[1])
    },

    setSavedPayments(state, savedPayments) {
      Vue.set(state.currentUser, 'savedPayments', savedPayments || [])
      const orderedSavedPayments = savedPayments.sort(payment => Date.parse(payment.createdAt))
      Vue.set(state, 'orderedSavedPayments', orderedSavedPayments)
    },
  },

  actions: {
    async fetchData({ state, dispatch }) {
      if (state.userFetchState !== FETCH_STATES.initial) return

      state.userFetchState = FETCH_STATES.fetching

      const {
        data: { currentUser, paths },
      } = await client.query({ query: currentUserQuery })

      dispatch('handleFetchData', { currentUser, paths })
    },

    async fetchSavedPayments({ commit }) {
      if (savedPaymentsFetchState !== FETCH_STATES.initial) return

      savedPaymentsFetchState = FETCH_STATES.fetching

      const {
        data: { currentUser },
      } = await client.query({ query: savedPaymentsQuery })

      commit('setSavedPayments', currentUser.savedPayments)

      savedPaymentsFetchState = FETCH_STATES.done
    },

    handleFetchData({ commit, state }, { currentUser, paths }) {
      commit('setUserData', currentUser)
      commit('setAccountPaths', paths)
      state.userFetchState = FETCH_STATES.done
    },

    createAddress({ commit, dispatch }, addressObject) {
      dispatch('updateIfAddressExists', addressObject).then(addressExists => {
        if (!addressExists) {
          if (addressObject.is_default) {
            dispatch('resetDefaultAddressFlag')
          }
          commit('pushToAddresses', addressObject)
        }
      })
    },

    updateIfAddressExists({ dispatch, state }, addressObject) {
      return new Promise(resolve => {
        const addressExists = state.currentUser.addresses.some(
          address => address.id == addressObject.id
        )

        if (addressExists) {
          dispatch('updateAddress', addressObject)
        }
        resolve(addressExists)
      })
    },

    updateAddress({ commit, dispatch, state }, addressObject) {
      const addressIndex = state.currentUser.addresses.findIndex(
        address => address.id == addressObject.id
      )

      if (addressObject.is_default) {
        dispatch('resetDefaultAddressFlag', addressIndex)
      }
      commit('setAddress', [addressIndex, addressObject])
    },

    resetDefaultAddressFlag({ commit, state }, defaultAddressIndex) {
      state.currentUser.addresses.forEach((address, i) => {
        if (i != defaultAddressIndex) {
          commit('setAddressIsDefault', [i, false])
        }
      })
    },

    deleteSavedPayment({ commit, state }, paymentReference) {
      return client
        .mutate({
          mutation: deleteSavedPaymentMutation,
          variables: { reference: paymentReference },
        })
        .then(
          ({
            data: {
              deleteSavedPayment: { success },
            },
          }) => {
            if (success) {
              const newSavedPayments = state.currentUser.savedPayments.filter(
                payment => payment.reference !== paymentReference
              )
              commit('setSavedPayments', newSavedPayments)
            }
          }
        )
    },

    /**
     * Use this action instead of doesEmailHaveAnAccount only if the user provides his own email
     * address and not the one from someone else (i.e. giving a friend's email for referral)
     */
    async doesCurrentUserHaveAnAccount({ dispatch }, email) {
      Vue.$dyClient.identifyUser(email)
      return await dispatch('doesEmailHaveAnAccount', email)
    },

    async doesEmailHaveAnAccount({ dispatch }, email) {
      const [error] = await to(dispatch('getUserByEmail', email))
      return !error
    },

    getUserByEmail({ commit }, email) {
      return client
        .query({ query: userExistsQuery, variables: { email } })
        .then(({ data: { userExists, paths } }) => {
          commit('setAccountPaths', paths)
          return userExists || Promise.reject(new Error('No user available for given email'))
        })
    },

    async loginUser({ dispatch, state }, { email, password, rememberMe }) {
      const data = {
        spree_user: {
          email,
          password,
          remember_me: rememberMe,
        },
      }
      const response = await Axios.post(state.paths.login, data)
      if (response.data?.success) Vue.$dyClient.trackAccountLogin(email)

      state.userFetchState = FETCH_STATES.initial
      await dispatch('fetchData')
      return response
    },
  },

  getters: {
    loggedIn: state => !!state.currentUser,
    currentUser: state => state.currentUser,
    savedAddresses: state => state.currentUser?.addresses || [],
    defaultSavedAddress: (state, getters) => getters.getDefaultSavedAddress(getters.savedAddresses),
    getDefaultSavedAddress: () => savedAddresses =>
      savedAddresses?.find(({ is_default }) => is_default) || savedAddresses?.[0],
    findSavedAddress: () => (savedAddresses = [], address) =>
      address
        ? savedAddresses.find(savedAddress =>
            areAddressesEqual(savedAddress, address, Object.keys(address))
          )
        : null,
    hasFetchedCurrentUser: state => state.hasFetchedCurrentUser,
    firstName: state => state.currentUser?.first_name || '',
    orderedSavedPayments: state => state.orderedSavedPayments || [],
    paths: state => state.paths,
    storeCredits: state => state.currentUser?.storeCredits || [],
    hasStoreCredits: (state, getters) => getters.storeCredits.length > 0,
    availableStoreCredits: (state, getters, rootState) =>
      getters.storeCredits.filter(
        ({ currency, amountAvailable }) =>
          currency.code === rootState.store.currency?.code && amountAvailable > 0
      ),
    hasAvailableStoreCredits: (state, getters) => getters.availableStoreCredits.length > 0,
    availableStoreCreditTotal: (state, getters) =>
      getters.availableStoreCredits.reduce(
        (totalAmount, { amountAvailable }) => totalAmount + amountAvailable,
        0
      ),
  },
}
